import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent, TextFieldProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import CrossIcon from "@/assets/icons/CrossIcon";
import { llmApi, projectsApi } from "@/services/rag-service";
import {
  CatalogFormSelect,
  CatalogFormTextField,
} from "./components/FormControls";
import ModelCapabilitySelector from "./components/ModelCapabilitySelector";
import ModelSpecificFields from "./components/ModelSpecificFields";
import { getModelFieldConfig, getModelTypes } from "./constants/modelFields";
import type {
  CatalogSelectOption,
  FormInputChangeEvent,
  LLMConfigFormData,
  LLMConfigRequestData,
  LLMConfigsResponse,
  LLMModel,
  ModelTypeKey,
  Project,
  Provider,
  NumericFormValue,
  TranslateFn,
} from "./types";
import {
  buildFormDataFromModel,
  buildPatchData,
  buildRequestData,
  defaultProvider,
  EXTRA_CONFIG_FORM_FIELDS,
  getApiErrorMessage,
  getDefaultExtraConfigFormValues,
  getExtraConfigFieldsForModelType,
  getFormDataModelTypeKey,
  getInitialFormData,
  MODEL_TYPE_KEYS,
  STT_RESPONSE_FORMATS,
} from "./utils/catalog";

type AICatalogFormProps = {
  selectedModelId: string | null;
  isPublic: boolean;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  title?: string;
  initialFormData?: Partial<LLMConfigFormData>;
  providersOverride?: Provider[];
  projectsOverride?: Project[];
  providerDisabled?: boolean;
  authHeaderDisabled?: boolean;
  projectPlaceholder?: string;
  apiKeyOptions?: CatalogSelectOption[];
  apiKeysLoading?: boolean;
  noApiKeysMessage?: string;
  apiKeyInputMode?: "text" | "select";
  submitLabel?: string;
  submittingLabel?: string;
  successMessage?: string;
  validateEndpointUrl?: boolean;
  skipDuplicateCheck?: boolean;
  allowedModelTypes?: ModelTypeKey[];
  showPublicAccessToggle?: boolean;
  onSubmitRequest?: (
    requestData: LLMConfigRequestData,
    formData: LLMConfigFormData,
  ) => void | Promise<void>;
  transformRequestData?: (
    requestData: LLMConfigRequestData,
    formData: LLMConfigFormData,
  ) => LLMConfigRequestData | Promise<LLMConfigRequestData>;
};

type FormErrors = Partial<Record<keyof LLMConfigFormData, string>>;

const MODEL_NAME_MAX_LENGTH = 100;
const MODEL_IDENTIFIER_MAX_LENGTH = 100;
const ENDPOINT_MAX_LENGTH = 2048;
const API_KEY_MAX_LENGTH = 512;
const AUTH_HEADER_MAX_LENGTH = 50;
const FORMAT_MAX_LENGTH = 20;
const AUTH_HEADER_BEARER = "bearer";
const AUTH_HEADER_NONE = "NONE";
// const MODEL_IDENTIFIER_PATTERN = /^[A-Za-z0-9.-]+$/;
const SAFE_TEXT_PATTERN = /<[^>]*>|javascript:/i;
const IMAGE_FORMAT_WHITELIST = new Set([
  "bmp",
  "gif",
  "jpeg",
  "jpg",
  "png",
  "tiff",
  "webp",
]);
const STT_RESPONSE_FORMAT_SET = new Set<string>(STT_RESPONSE_FORMATS);
const NUMERIC_FORM_FIELDS = new Set<keyof LLMConfigFormData>([
  "temperature",
  "top_p",
  "max_tokens",
  "context_length",
  "max_output_tokens",
  "max_input_tokens",
  "embedding_dimension",
  "max_image_size",
  "max_images_per_request",
  "max_input_characters",
  "max_audio_duration_seconds",
  "max_audio_size",
]);

const getNumericValue = (value: NumericFormValue): number | null => {
  if (value === "" || value === ".") return null;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const isIntegerAtLeast = (value: NumericFormValue, min: number): boolean => {
  const numericValue = getNumericValue(value);
  return (
    numericValue !== null &&
    Number.isInteger(numericValue) &&
    numericValue >= min
  );
};

const isNumberInRange = (
  value: NumericFormValue,
  min: number,
  max: number,
): boolean => {
  const numericValue = getNumericValue(value);
  return numericValue !== null && numericValue >= min && numericValue <= max;
};

const hasAtMostTwoDecimalPlaces = (value: NumericFormValue): boolean => {
  if (getNumericValue(value) === null) return false;
  return /^\d+(\.\d{0,2})?$/.test(String(value));
};

const hasDuplicates = (values: string[]): boolean => {
  const normalizedValues = values.map((value) => value.trim().toLowerCase());
  return new Set(normalizedValues).size !== normalizedValues.length;
};

const validateFormats = (
  values: string[],
  t: TranslateFn,
  whitelist: Set<string>,
  whitelistMessageKey: string,
  maxItems?: number,
): string | undefined => {
  const trimmedValues = values.map((value) => value.trim()).filter(Boolean);

  if (trimmedValues.length === 0) {
    return undefined;
  }
  if (maxItems !== undefined && trimmedValues.length > maxItems) {
    return t("modelRegistration.supportedFormatsSingleValue");
  }
  if (trimmedValues.some((value) => value.length > FORMAT_MAX_LENGTH)) {
    return t("modelRegistration.supportedFormatMaxLength", {
      count: FORMAT_MAX_LENGTH,
    });
  }
  if (trimmedValues.some((value) => !whitelist.has(value.toLowerCase()))) {
    return t(whitelistMessageKey);
  }
  if (hasDuplicates(trimmedValues)) {
    return t("modelRegistration.supportedFormatsDuplicate");
  }

  return undefined;
};

const validateForm = (
  formData: LLMConfigFormData,
  isPublic: boolean,
  isEditMode: boolean,
  t: TranslateFn,
  options: { validateEndpointUrl: boolean } = { validateEndpointUrl: true },
): FormErrors => {
  const errors: FormErrors = {};
  const trimmedName = formData.name.trim();
  const trimmedModel = formData.model.trim();
  const trimmedEndpoint = formData.endpoint.trim();
  const trimmedApiKey = formData.api_key.trim();
  const trimmedAuthHeaderType = formData.auth_header_type.trim();
  const contextLength = getNumericValue(formData.context_length);
  const maxTokens = getNumericValue(formData.max_tokens);
  const maxOutputTokens = getNumericValue(formData.max_output_tokens);
  const maxAudioDurationSeconds = getNumericValue(
    formData.max_audio_duration_seconds,
  );

  if (!trimmedName) {
    errors.name = t("modelRegistration.nameRequired");
  } else if (trimmedName.length > MODEL_NAME_MAX_LENGTH) {
    errors.name = t("modelRegistration.nameMaxLength", {
      count: MODEL_NAME_MAX_LENGTH,
    });
  } else if (SAFE_TEXT_PATTERN.test(trimmedName)) {
    errors.name = t("modelRegistration.nameInvalidCharacters");
  }

  if (!trimmedModel) {
    errors.model = t("modelRegistration.modelIdentifierRequired");
  } else if (trimmedModel.length > MODEL_IDENTIFIER_MAX_LENGTH) {
    errors.model = t("modelRegistration.modelIdentifierMaxLength", {
      count: MODEL_IDENTIFIER_MAX_LENGTH,
    });
  }
  // else if (!MODEL_IDENTIFIER_PATTERN.test(trimmedModel)) {
  //   errors.model = t("modelRegistration.modelIdentifierInvalidCharacters");
  // }

  if (!trimmedEndpoint) {
    errors.endpoint = t("modelRegistration.endpointRequired");
  } else if (trimmedEndpoint.length > ENDPOINT_MAX_LENGTH) {
    errors.endpoint = t("modelRegistration.endpointMaxLength", {
      count: ENDPOINT_MAX_LENGTH,
    });
  } else if (options.validateEndpointUrl) {
    try {
      const url = new URL(trimmedEndpoint);
      if (!["http:", "https:"].includes(url.protocol)) {
        errors.endpoint = t("modelRegistration.endpointInvalidUrl");
      }
    } catch {
      errors.endpoint = t("modelRegistration.endpointInvalidUrl");
    }
  }

  if (!isPublic && !formData.project_id) {
    errors.project_id = t("modelRegistration.projectRequired");
  }
  if (!formData.provider) {
    errors.provider = t("modelRegistration.providerRequired");
  }

  const isNoAuthHeader = formData.auth_header_type === AUTH_HEADER_NONE;

  if (!isNoAuthHeader && !isEditMode && !trimmedApiKey) {
    errors.api_key = t("aiCatalog.apiKeyRequired");
  } else if (!isNoAuthHeader && trimmedApiKey.length > API_KEY_MAX_LENGTH) {
    errors.api_key = t("modelRegistration.apiKeyMaxLength", {
      count: API_KEY_MAX_LENGTH,
    });
  } else if (
    !isNoAuthHeader &&
    !isEditMode &&
    trimmedAuthHeaderType &&
    !trimmedApiKey
  ) {
    errors.api_key = t("modelRegistration.apiKeyRequiredWithAuth");
  }

  if (
    formData.auth_header_type !== AUTH_HEADER_BEARER &&
    !isNoAuthHeader &&
    !trimmedAuthHeaderType
  ) {
    errors.auth_header_type = t("modelRegistration.customHeaderRequired");
  } else if (trimmedAuthHeaderType.length > AUTH_HEADER_MAX_LENGTH) {
    errors.auth_header_type = t("modelRegistration.authHeaderMaxLength", {
      count: AUTH_HEADER_MAX_LENGTH,
    });
  }

  if (
    formData.is_embedding_model &&
    formData.embedding_dimension !== "" &&
    !isIntegerAtLeast(formData.embedding_dimension, 1)
  ) {
    errors.embedding_dimension = t(
      "modelRegistration.embeddingDimensionPositive",
    );
  }

  if (formData.is_chat_model || formData.is_multimodal) {
    if (
      !isNumberInRange(formData.temperature, 0, 1) ||
      !hasAtMostTwoDecimalPlaces(formData.temperature)
    ) {
      errors.temperature = t("modelRegistration.temperatureRange");
    }

    if (
      formData.context_length !== "" &&
      !isIntegerAtLeast(formData.context_length, 1)
    ) {
      errors.context_length = t("modelRegistration.contextLengthInvalid");
    }

    if (
      formData.max_tokens !== "" &&
      !isIntegerAtLeast(formData.max_tokens, 0)
    ) {
      errors.max_tokens = t("modelRegistration.maxTokensInvalid");
    } else if (
      contextLength !== null &&
      maxTokens !== null &&
      maxTokens > 0 &&
      maxTokens > contextLength
    ) {
      errors.max_tokens = t("modelRegistration.maxTokensExceedsContext");
    }

    if (
      formData.max_output_tokens !== "" &&
      !isIntegerAtLeast(formData.max_output_tokens, 0)
    ) {
      errors.max_output_tokens = t("modelRegistration.maxOutputTokensInvalid");
    } else if (
      maxTokens !== null &&
      maxOutputTokens !== null &&
      maxTokens > 0 &&
      maxOutputTokens > 0 &&
      maxOutputTokens > maxTokens
    ) {
      errors.max_output_tokens = t(
        "modelRegistration.maxOutputTokensExceedsMax",
      );
    }

    if (formData.top_p !== "" && !isNumberInRange(formData.top_p, 0, 1)) {
      errors.top_p = t("modelRegistration.topPRange");
    }
  }

  if (
    formData.is_multimodal &&
    formData.max_image_size !== "" &&
    !isNumberInRange(formData.max_image_size, 0.1, 20)
  ) {
    errors.max_image_size = t("modelRegistration.maxImageSizeInvalid");
  }

  if (
    formData.is_tts_model &&
    formData.max_input_characters !== "" &&
    !isIntegerAtLeast(formData.max_input_characters, 1)
  ) {
    errors.max_input_characters = t(
      "modelRegistration.maxInputCharactersInvalid",
    );
  }

  if (formData.is_image_generation_model) {
    const formatError = validateFormats(
      formData.output_formats,
      t,
      IMAGE_FORMAT_WHITELIST,
      "modelRegistration.outputFormatWhitelist",
    );
    if (formatError) errors.output_formats = formatError;
  }

  if (
    formData.is_stt_model &&
    formData.max_audio_duration_seconds !== "" &&
    (!isIntegerAtLeast(formData.max_audio_duration_seconds, 1) ||
      (maxAudioDurationSeconds !== null && maxAudioDurationSeconds > 3600))
  ) {
    errors.max_audio_duration_seconds = t(
      "modelRegistration.maxAudioDurationInvalid",
    );
  }

  if (
    formData.is_stt_model &&
    formData.max_audio_size !== "" &&
    !isNumberInRange(formData.max_audio_size, 0.1, 20)
  ) {
    errors.max_audio_size = t("modelRegistration.maxAudioSizeInvalid");
  }

  if (formData.is_stt_model) {
    const responseFormat = formData.supported_audio_format[0]
      ?.trim()
      .toLowerCase() ?? "";
    if (responseFormat && !STT_RESPONSE_FORMAT_SET.has(responseFormat)) {
      errors.supported_audio_format = t(
        "modelRegistration.sttResponseFormatInvalid",
      );
    }
  }

  return errors;
};

const AICatalogFormComp = ({
  selectedModelId,
  isPublic,
  open,
  onClose,
  onSuccess,
  title,
  initialFormData,
  providersOverride,
  projectsOverride,
  providerDisabled = false,
  authHeaderDisabled = false,
  projectPlaceholder,
  apiKeyOptions,
  apiKeysLoading = false,
  noApiKeysMessage,
  apiKeyInputMode = "text",
  submitLabel,
  submittingLabel,
  successMessage,
  validateEndpointUrl = true,
  skipDuplicateCheck = false,
  allowedModelTypes,
  showPublicAccessToggle = false,
  onSubmitRequest,
  transformRequestData,
}: AICatalogFormProps) => {
  const { t } = useTranslation();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<LLMConfigFormData>(() => ({
    ...getInitialFormData(isPublic),
    ...initialFormData,
  }));
  const [originalFormData, setOriginalFormData] =
    useState<LLMConfigFormData | null>(null);

  const modelTypes = useMemo(() => {
    const types = getModelTypes(t);
    if (!allowedModelTypes) return types;

    return types.filter(({ key }) => allowedModelTypes.includes(key));
  }, [allowedModelTypes, t]);
  const modelFieldConfig = useMemo(() => getModelFieldConfig(t), [t]);
  const activeModelType = useMemo(
    () => MODEL_TYPE_KEYS.find((key) => formData[key]) || null,
    [formData],
  );
  const isEditMode = Boolean(selectedModelId);
  const effectiveIsPublic = showPublicAccessToggle
    ? formData.is_public
    : isPublic;
  const activeModelDescription = useMemo(
    () => modelTypes.find(({ key }) => key === activeModelType)?.desc,
    [activeModelType, modelTypes],
  );
  const fetchProviders = async () => {
    if (providersOverride) {
      setProviders(providersOverride);
      setProvidersLoading(false);
      return;
    }

    setProvidersLoading(true);
    try {
      const response = (await llmApi.getProviders()) as Provider[];
      setProviders(response || []);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, t("modelRegistration.failedToLoadProviders")),
      );
      setProviders([defaultProvider]);
    } finally {
      setProvidersLoading(false);
    }
  };

  const fetchProjects = async () => {
    if (projectsOverride) {
      setProjects(projectsOverride);
      setProjectsLoading(false);
      return;
    }

    setProjectsLoading(true);
    try {
      const response = (await projectsApi.getProjects()) as Project[];
      setProjects(response || []);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, t("projects.failedToLoadProjects")),
      );
    } finally {
      setProjectsLoading(false);
    }
  };

  const getLLMConfig = async () => {
    if (!selectedModelId) return;

    try {
      setSubmitting(true);
      const response = (await llmApi.getLLMConfig(
        selectedModelId,
      )) as unknown as LLMModel;
      const loadedFormData = buildFormDataFromModel(response, isPublic);
      setFormData(loadedFormData);
      setOriginalFormData(loadedFormData);
    } catch (error) {
      console.error("Error fetching models:", error);
      toast.error(t("aiCatalog.failedToLoadModel"));
    } finally {
      setSubmitting(false);
    }
  };

  const getFilteredProviders = () => providers;

  const clearFieldError = (field: keyof LLMConfigFormData) => {
    setFormErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const handleChipChange =
    (field: keyof LLMConfigFormData) =>
    (_event: React.SyntheticEvent, newValue: string[]) => {
      clearFieldError(field);
      setFormData((previous) => ({
        ...previous,
        [field]: newValue,
      }));
    };

  const handleInputChange = (event: FormInputChangeEvent) => {
    const { name, value } = event.target;
    const inputType = "type" in event.target ? event.target.type : undefined;
    const isNumericField = NUMERIC_FORM_FIELDS.has(
      name as keyof LLMConfigFormData,
    );
    const checked =
      "checked" in event.target ? event.target.checked : undefined;

    clearFieldError(name as keyof LLMConfigFormData);
    setFormData((previous) => ({
      ...previous,
      [name]:
        inputType === "number" || isNumericField
          ? value
          : inputType === "checkbox"
            ? Boolean(checked)
            : value,
    }));
  };

  const handleClose = () => {
    if (submitting) return;
    setFormErrors({});
    setFormData({
      ...getInitialFormData(isPublic),
      ...initialFormData,
    });
    setOriginalFormData(null);
    onClose();
  };

  const getDuplicateFieldErrors = async (): Promise<FormErrors> => {
    const options: Record<string, unknown> = {
      limit: 1000,
      include_public: effectiveIsPublic,
      include_shared: false,
    };

    if (!effectiveIsPublic && formData.project_id) {
      options.project_ids = [formData.project_id];
    }

    const response = (await llmApi.getLLMConfigs(options as any)) as unknown as
      | LLMConfigsResponse
      | LLMModel[];
    const models = Array.isArray(response) ? response : response.items || [];
    const trimmedName = formData.name.trim().toLowerCase();

    const scopedModels = models.filter((model) => {
      if (model.id === selectedModelId) return false;

      return effectiveIsPublic
        ? Boolean(model.is_public)
        : !model.is_public && model.project_id === formData.project_id;
    });

    const duplicateName = scopedModels.find(
      (model) => model.name.trim().toLowerCase() === trimmedName,
    );

    if (!duplicateName) return {};

    const errors: FormErrors = {};
    if (duplicateName) {
      errors.name = t("modelRegistration.nameDuplicateInScope");
    }

    return errors;
  };

  const handleSubmit = async () => {
    if (selectedModelId && !hasUnsavedChanges) return;

    const nextFormErrors = validateForm(
      formData,
      effectiveIsPublic,
      isEditMode,
      t,
      {
        validateEndpointUrl,
      },
    );

    if (Object.keys(nextFormErrors).length > 0) {
      setFormErrors(nextFormErrors);
      return;
    }

    setSubmitting(true);
    setFormErrors({});

    try {
      if (!skipDuplicateCheck) {
        const duplicateFieldErrors = await getDuplicateFieldErrors();
        if (Object.keys(duplicateFieldErrors).length > 0) {
          setFormErrors(duplicateFieldErrors);
          return;
        }
      }

      const builtRequestData = buildRequestData(formData, effectiveIsPublic);
      const requestData = transformRequestData
        ? await transformRequestData(builtRequestData, formData)
        : builtRequestData;

      if (onSubmitRequest) {
        await onSubmitRequest(requestData, formData);
      } else if (selectedModelId) {
        if (!originalFormData) return;
        const originalRequestData = buildRequestData(
          originalFormData,
          showPublicAccessToggle ? originalFormData.is_public : isPublic,
        );
        const patchData = buildPatchData(requestData, originalRequestData);
        if (Object.keys(patchData).length === 0) return;
        await llmApi.updateLLMConfig(selectedModelId, patchData as any);
      } else if (isPublic) {
        await llmApi.createAdminLLMConfig(requestData as any);
      } else {
        await llmApi.createUserLLMConfig(requestData as any);
      }

      toast.success(successMessage || t("modelRegistration.registerSuccess"));
      if (selectedModelId) setOriginalFormData(formData);
      setFormData({
        ...getInitialFormData(isPublic),
        ...initialFormData,
      });
      await onSuccess();
      onClose();
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        t("modelRegistration.registerFailed"),
      );
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTypeSelect = (key: ModelTypeKey) => {
    if (submitting || formData[key]) return;

    setFormErrors((previous) => {
      const next = { ...previous };
      EXTRA_CONFIG_FORM_FIELDS.forEach((field) => {
        delete next[field as keyof LLMConfigFormData];
      });
      return next;
    });
    setFormData((previous) => ({
      ...previous,
      ...getDefaultExtraConfigFormValues(),
      ...(originalFormData && getFormDataModelTypeKey(originalFormData) === key
        ? getExtraConfigFieldsForModelType(key).reduce(
            (values, field) => ({
              ...values,
              [field]: originalFormData[field],
            }),
            {},
          )
        : {}),
      is_image_generation_model: false,
      is_chat_model: false,
      is_embedding_model: false,
      is_stt_model: false,
      is_tts_model: false,
      is_multimodal: false,
      [key]: true,
    }));
  };

  const handleAuthHeaderChange = (event: SelectChangeEvent<string>) => {
    const selectedAuthHeader = event.target.value;
    clearFieldError("auth_header_type");
    clearFieldError("api_key");
    setFormData((previous) => ({
      ...previous,
      auth_header_type:
        selectedAuthHeader === AUTH_HEADER_BEARER ||
        selectedAuthHeader === AUTH_HEADER_NONE
          ? selectedAuthHeader
          : "",
      api_key: selectedAuthHeader === AUTH_HEADER_NONE ? "" : previous.api_key,
    }));
  };

  const getAuthHeaderSelectValue = () => {
    if (formData.auth_header_type === AUTH_HEADER_BEARER) {
      return AUTH_HEADER_BEARER;
    }
    if (formData.auth_header_type === AUTH_HEADER_NONE) {
      return AUTH_HEADER_NONE;
    }

    return "custom";
  };

  const isCustomAuthHeader =
    formData.auth_header_type !== AUTH_HEADER_BEARER &&
    formData.auth_header_type !== AUTH_HEADER_NONE;
  const shouldShowApiKey = formData.auth_header_type !== AUTH_HEADER_NONE;

  useEffect(() => {
    if (formData.auth_header_type !== AUTH_HEADER_NONE || !formData.api_key) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      api_key: "",
    }));
  }, [formData.api_key, formData.auth_header_type]);

  useEffect(() => {
    if (!open) return;

    setFormErrors({});
    if (selectedModelId) getLLMConfig();
    else {
      setOriginalFormData(null);
      setFormData({
        ...getInitialFormData(isPublic),
        ...initialFormData,
      });
    }
    fetchProviders();
    fetchProjects();
  }, [
    open,
    isPublic,
    selectedModelId,
    initialFormData,
    providersOverride,
    projectsOverride,
  ]);

  useEffect(() => {
    if (!open || apiKeyInputMode !== "select" || !shouldShowApiKey) return;

    const options = apiKeyOptions || [];
    const hasSelectedOption = options.some(
      (option) => String(option.value) === formData.api_key,
    );

    if (!options.length && formData.api_key) {
      setFormData((previous) => ({
        ...previous,
        api_key: "",
      }));
      return;
    }

    if (!options.length || hasSelectedOption) return;

    setFormData((previous) => ({
      ...previous,
      api_key: String(options[0].value),
    }));
  }, [
    apiKeyInputMode,
    apiKeyOptions,
    formData.api_key,
    open,
    shouldShowApiKey,
  ]);

  const projectOptions: CatalogSelectOption[] = projects.map((project) => ({
    label: project.name,
    value: project.id,
  }));
  const providerOptions: CatalogSelectOption[] = getFilteredProviders().map(
    (provider) => ({
      label: provider.name,
      value: provider.name,
    }),
  );
  const resolvedTitle =
    title ||
    (selectedModelId
      ? isPublic
        ? t("aiCatalog.updatePublicModel")
        : t("aiCatalog.updatePrivateModel")
      : isPublic
        ? t("aiCatalog.registerPublicModel")
        : t("aiCatalog.registerPrivateModel"));
  const hasUnsavedChanges = useMemo(() => {
    if (!selectedModelId) return true;
    if (!originalFormData) return false;

    const currentRequestData = buildRequestData(formData, effectiveIsPublic);
    const originalRequestData = buildRequestData(
      originalFormData,
      showPublicAccessToggle ? originalFormData.is_public : isPublic,
    );
    return (
      Object.keys(buildPatchData(currentRequestData, originalRequestData))
        .length > 0
    );
  }, [
    effectiveIsPublic,
    formData,
    isPublic,
    originalFormData,
    selectedModelId,
    showPublicAccessToggle,
  ]);
  const disableSubmit =
    submitting ||
    providersLoading ||
    projectsLoading ||
    apiKeysLoading ||
    (isEditMode && !hasUnsavedChanges) ||
    (shouldShowApiKey &&
      apiKeyInputMode === "select" &&
      (apiKeyOptions?.length || 0) === 0);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className:
          "w-[850px] max-w-[calc(100vw_-_32px)] rounded-lg border border-[var(--border)] bg-[var(--card)] text-[var(--foreground)] shadow-[0_24px_80px_rgba(0,0,0,0.36)]",
      }}
    >
      <DialogTitle>
        <Box className="flex items-center justify-between">
          <Typography className="text-[20px] font-semibold text-[var(--card-foreground)]">
            {resolvedTitle}
          </Typography>
          <Box
            className="cursor-pointer"
            onClick={handleClose}
            data-testid="ai-catalog-form-close-button"
          >
            <CrossIcon className="h-8 w-8 text-[var(--muted-foreground)]" />
          </Box>
        </Box>
      </DialogTitle>
      <Box className="px-6 py-2">
        <Box className="mb-4">
          <Typography className="text-sm font-medium text-[var(--foreground)] mb-3">
            {t("modelRegistration.basicInformation")}
          </Typography>
          <Grid container spacing={1}>
            <CatalogFormTextField
              label={t("modelRegistration.modelName")}
              name="name"
              value={formData.name}
              onChange={handleInputChange as TextFieldProps["onChange"]}
              disabled={submitting}
              placeholder={t("modelRegistration.modelName")}
              inputProps={{ maxLength: MODEL_NAME_MAX_LENGTH }}
              required
              grid={{ xs: 12, md: effectiveIsPublic ? 12 : 6 }}
              testId="ai-catalog-form-input-name"
              error={Boolean(formErrors.name)}
              helperText={formErrors.name}
            />
            {!effectiveIsPublic ? (
              <CatalogFormSelect
                label={t("common.project")}
                name="project_id"
                value={formData.project_id}
                onChange={handleInputChange}
                disabled={submitting}
                placeholder={
                  projectsLoading
                    ? t("common.loading")
                    : projectPlaceholder || t("aiCatalog.selectProject")
                }
                required
                grid={{ xs: 12, md: 6 }}
                options={projectOptions}
                testId="ai-catalog-form-select-project"
                optionTestIdPrefix="ai-catalog-form-select-project-option"
                error={Boolean(formErrors.project_id)}
                helperText={formErrors.project_id}
              />
            ) : null}
            <CatalogFormTextField
              label={t("modelRegistration.modelIdentifier")}
              name="model"
              value={formData.model}
              onChange={handleInputChange as TextFieldProps["onChange"]}
              disabled={submitting}
              placeholder={t("modelRegistration.modelIdentifierPlaceholder")}
              inputProps={{ maxLength: MODEL_IDENTIFIER_MAX_LENGTH }}
              required
              grid={{ xs: 12, md: 6 }}
              testId="ai-catalog-form-input-model"
              error={Boolean(formErrors.model)}
              helperText={formErrors.model}
            />
            <CatalogFormTextField
              label={t("modelRegistration.endpointUrl")}
              name="endpoint"
              value={formData.endpoint}
              onChange={handleInputChange as TextFieldProps["onChange"]}
              disabled={submitting}
              placeholder={t("modelRegistration.endpointUrlPlaceholder")}
              inputProps={{ maxLength: ENDPOINT_MAX_LENGTH }}
              required
              className="url-textfield"
              grid={{ xs: 12, md: 6 }}
              testId="ai-catalog-form-input-endpoint"
              error={Boolean(formErrors.endpoint)}
              helperText={formErrors.endpoint}
            />
            <CatalogFormSelect
              label={t("modelRegistration.authHeaderType")}
              name="auth_header_type"
              value={getAuthHeaderSelectValue()}
              onChange={handleAuthHeaderChange}
              disabled={submitting || authHeaderDisabled}
              grid={{ xs: 4, md: 3 }}
              options={[
                { label: "Bearer", value: "bearer" },
                { label: "None", value: AUTH_HEADER_NONE },
                { label: t("common.custom"), value: "custom" },
              ]}
              testId="ai-catalog-form-select-auth-header-type"
            />

            {isCustomAuthHeader && (
              <CatalogFormTextField
                label={t("apiKeyDialog.customHeader")}
                name="auth_header_type"
                value={formData.auth_header_type || ""}
                onChange={handleInputChange as TextFieldProps["onChange"]}
                disabled={submitting || authHeaderDisabled}
                placeholder={t("apiKeyDialog.customHeaderPlaceholder")}
                inputProps={{ maxLength: AUTH_HEADER_MAX_LENGTH }}
                grid={{ xs: 4, md: 3 }}
                testId="ai-catalog-form-input-custom-auth-header"
                error={Boolean(formErrors.auth_header_type)}
                helperText={formErrors.auth_header_type}
                required
              />
            )}
            {shouldShowApiKey && apiKeyInputMode === "select" ? (
              <Grid
                item
                xs={isCustomAuthHeader ? 4 : 8}
                md={isCustomAuthHeader ? 6 : 9}
              >
                <Typography className="text-xs font-medium text-[var(--secondary-foreground)] mb-1">
                  {t("modelRegistration.apiKey")} *
                </Typography>
                <Select
                  data-testid="ai-catalog-form-select-api-key"
                  name="api_key"
                  value={formData.api_key}
                  onChange={handleInputChange}
                  fullWidth
                  required
                  disabled={submitting || apiKeysLoading}
                  error={Boolean(formErrors.api_key)}
                  className="mb-1"
                  displayEmpty
                >
                  <MenuItem disabled value="">
                    {apiKeysLoading
                      ? t("registerModelForRag.loadingApiKeys")
                      : noApiKeysMessage || t("modelRegistration.apiKey")}
                  </MenuItem>
                  {(apiKeyOptions || []).map((option, index) => (
                    <MenuItem
                      data-testid={`ai-catalog-form-select-api-key-option-${index}`}
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.api_key ? (
                  <Typography color="error" variant="caption">
                    {formErrors.api_key}
                  </Typography>
                ) : null}
              </Grid>
            ) : shouldShowApiKey ? (
              <CatalogFormTextField
                label={t("modelRegistration.apiKey")}
                name="api_key"
                value={formData.api_key}
                onChange={handleInputChange as TextFieldProps["onChange"]}
                disabled={submitting}
                placeholder={t(
                  isEditMode
                    ? "modelRegistration.apiKeyEditPlaceholder"
                    : "modelRegistration.apiKeyPlaceholder",
                )}
                type="password"
                inputProps={{ maxLength: API_KEY_MAX_LENGTH }}
                grid={{
                  xs: isCustomAuthHeader ? 4 : 8,
                  md: isCustomAuthHeader ? 6 : 9,
                }}
                testId="ai-catalog-form-input-api-key"
                error={Boolean(formErrors.api_key)}
                helperText={
                  formErrors.api_key ||
                  (isEditMode ? t("modelRegistration.apiKeyEditHelper") : "")
                }
                required={!isEditMode}
              />
            ) : null}
          </Grid>
        </Box>

        <Box>
          <Typography className="text-sm font-medium text-[var(--foreground)] mb-3">
            {t("modelRegistration.providerAndCapabilities")}
          </Typography>
          <Grid container spacing={1}>
            <CatalogFormSelect
              label={t("modelRegistration.provider")}
              name="provider"
              value={formData.provider}
              onChange={handleInputChange}
              disabled={submitting || providersLoading || providerDisabled}
              placeholder={
                providersLoading
                  ? t("modelRegistration.loadingProviders")
                  : providerOptions.length > 0
                    ? t("aiCatalog.selectProvider")
                    : t("modelRegistration.noCompatibleProviders")
              }
              required
              grid={{ xs: 12, md: 12 }}
              options={providerOptions}
              testId="ai-catalog-form-select-provider"
              error={Boolean(formErrors.provider)}
              helperText={formErrors.provider}
            />

            <ModelCapabilitySelector
              formData={formData}
              modelTypes={modelTypes}
              activeDescription={activeModelDescription}
              disabled={submitting}
              onSelect={handleTypeSelect}
            />

            <ModelSpecificFields
              activeModelType={activeModelType}
              fieldsByType={modelFieldConfig}
              formData={formData}
              formErrors={formErrors}
              disabled={submitting}
              onInputChange={handleInputChange as TextFieldProps["onChange"]}
              onChipChange={handleChipChange}
            />

            {showPublicAccessToggle ? (
              <Grid item xs={12} md={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_public"
                      checked={formData.is_public}
                      onChange={handleInputChange}
                      disabled={submitting}
                      color="success"
                    />
                  }
                  label={t("aiCatalog.public")}
                />
                <Typography className="text-xs font-normal text-[var(--muted-foreground)]">
                  {t("modelRegistration.publicAccessDescription")}
                </Typography>
              </Grid>
            ) : null}
          </Grid>
        </Box>

        <Box className="flex items-center justify-end gap-2 mt-4">
          <Button
            data-testid="ai-catalog-form-cancel-button"
            color="secondary"
            variant="text"
            onClick={handleClose}
            disabled={submitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            data-testid="ai-catalog-form-submit-button"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={disableSubmit}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting
              ? submittingLabel ||
                (selectedModelId
                  ? t("modelRegistration.updating")
                  : t("modelRegistration.registering"))
              : submitLabel ||
                (selectedModelId
                  ? t("aiCatalog.updateModel")
                  : t("aiCatalog.registerModel"))}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AICatalogFormComp;
